import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getSession } from "next-auth/react";
import { resetAllStoreData } from "@/store/resetActions";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";

const API_BASE_URL = "";
const GET_AVAILABILITY_PATH = "/api/availability/get-availability";
const SAVE_AVAILABILITY_PATH = "/api/availability/add-availability";

const buildApiUrl = (path) => {
  const base = API_BASE_URL.trim();
  if (!base) return path;
  return `${base.replace(/\/$/, "")}${path}`;
};

const getErrorMessageFromResponse = async (response, fallbackMessage) => {
  try {
    const text = await response.text();
    if (!text) return fallbackMessage;
    try {
      const json = JSON.parse(text);
      return json?.error || json?.message || fallbackMessage;
    } catch {
      return text;
    }
  } catch {
    return fallbackMessage;
  }
};

// Browsers word the same fetch transport failure differently:
// Chrome "Failed to fetch", Safari "Load failed", Firefox "NetworkError ...".
const isNetworkError = (error) =>
  error?.name === "TypeError" &&
  /failed to fetch|load failed|network(error| request failed)/i.test(
    error?.message || "",
  );

const normalizeNetworkError = (error, fallbackMessage) => {
  if (error?.name === "AbortError") {
    return "Request timed out. Please try again.";
  }

  if (isNetworkError(error)) {
    const offlineHint =
      typeof navigator !== "undefined" &&
      navigator &&
      "onLine" in navigator &&
      !navigator.onLine
        ? " You appear to be offline."
        : "";
    return `${fallbackMessage}. Network request failed.${offlineHint}`;
  }

  return error?.message || fallbackMessage;
};

const getErrorCategory = (error) => {
  if (error?.httpStatus) return "http";
  if (error?.name === "AbortError") return "timeout";
  if (isNetworkError(error)) return "network";
  if ((error?.message || "").toLowerCase().includes("no session found"))
    return "auth_session";
  return "unknown";
};

// Transient transport failures (offline, tab suspended, navigating away) are
// already tracked as analytics events, so keep them out of console.error,
// which the production wrapper forwards to PostHog as an exception.
const logAvailabilityError = (message, error) => {
  const isTransient = isNetworkError(error) || error?.name === "AbortError";
  // eslint-disable-next-line no-console
  (isTransient ? console.warn : console.error)(message, error);
};

const trackAvailabilityFailure = ({
  operation,
  error,
  requestUrl,
  options = {},
}) => {
  try {
    const url = requestUrl
      ? new URL(
          requestUrl,
          typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost",
        )
      : null;

    track(EVENTS.CLIENT_ERROR, {
      area: "availability",
      operation,
      error_category: getErrorCategory(error),
      error_name: error?.name || "Error",
      error_message: error?.message || "Unknown error",
      http_status: error?.httpStatus || undefined,
      endpoint_path: url?.pathname || undefined,
      endpoint_origin: url?.origin || undefined,
      has_user_id_param: Boolean(options?.userId),
      online:
        typeof navigator !== "undefined" && navigator && "onLine" in navigator
          ? Boolean(navigator.onLine)
          : undefined,
    });
  } catch {
    // Analytics must never interrupt app behavior.
  }
};

const convertFirestoreTimestamps = (data) => {
  return data;
};

export const fetchAvailability = createAsyncThunk(
  "availability/fetchAvailability",
  async (options = {}, { getState, rejectWithValue }) => {
    let requestUrl = "";
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      const url = new URL(
        buildApiUrl(GET_AVAILABILITY_PATH),
        typeof window !== "undefined"
          ? window.location.origin
          : "http://localhost",
      );

      if (options.userId) {
        url.searchParams.set("userId", String(options.userId));
      }

      requestUrl = url.toString();

      const response = await fetch(requestUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const message = await getErrorMessageFromResponse(
          response,
          "Failed to fetch availability",
        );
        const httpError = new Error(message);
        httpError.httpStatus = response.status;
        throw httpError;
      }

      const data = await response.json();
      return convertFirestoreTimestamps(data);
    } catch (error) {
      logAvailabilityError("Error fetching availability:", error);
      trackAvailabilityFailure({
        operation: "fetch_availability",
        error,
        requestUrl,
        options,
      });
      return rejectWithValue(
        normalizeNetworkError(error, "Failed to fetch availability"),
      );
    }
  },
);

export const saveAvailability = createAsyncThunk(
  "availability/saveAvailability",
  async (availabilityData, { getState, rejectWithValue }) => {
    let requestUrl = "";
    try {
      const session = await getSession();

      if (!session?.user?.sub) {
        throw new Error("No session found");
      }

      requestUrl = buildApiUrl(SAVE_AVAILABILITY_PATH);

      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(availabilityData),
      });

      if (!response.ok) {
        const message = await getErrorMessageFromResponse(
          response,
          "Failed to save availability",
        );
        const httpError = new Error(message);
        httpError.httpStatus = response.status;
        throw httpError;
      }

      const result = await response.json();
      return convertFirestoreTimestamps(result);
    } catch (error) {
      logAvailabilityError("Error saving availability:", error);
      trackAvailabilityFailure({
        operation: "save_availability",
        error,
        requestUrl,
      });
      return rejectWithValue(
        normalizeNetworkError(error, "Failed to save availability"),
      );
    }
  },
);

const initialState = {
  data: null,
  hasFetched: false,
  status: "idle",
  error: null,
};

export const AvailabilityDataSlice = createSlice({
  name: "availability",
  initialState,
  reducers: {
    getAvailability: (state, action) => {
      state.data = action.payload;
      state.hasFetched = true;
    },
    deleteAvailability: (state, action) => {
      if (state.data && typeof state.data === "object") {
        const dayToDelete = action.payload;
        if (state.data[dayToDelete]) {
          delete state.data[dayToDelete];
        }
      }
    },
    addAvailability: (state, action) => {
      if (state.data && typeof state.data === "object") {
        const { day, availability } = action.payload;
        state.data[day] = availability;
      }
    },
    updateTimesOptimistic: (state, action) => {
      if (state.data && typeof state.data === "object") {
        const { times } = action.payload;
        if (!state.data.times) state.data.times = {};
        state.data.times = { ...state.data.times, ...times };
      }
    },
    invalidateAvailabilityCache: (state) => {
      state.hasFetched = false;
      state.status = "idle";
    },
    clearAvailabilityError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailability.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAvailability.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
        state.hasFetched = true;
        state.error = null;
      })
      .addCase(fetchAvailability.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(saveAvailability.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveAvailability.fulfilled, (state, action) => {
        state.status = "succeeded";

        if (action.payload) {
          const newData = {
            ...state.data,
          };

          if (action.payload.times) {
            newData.times = action.payload.times;
          }

          if (action.payload.blocked_periods !== undefined) {
            newData.blocked_periods = action.payload.blocked_periods;
          }

          if (action.payload.window !== undefined) {
            newData.window = action.payload.window;
          }

          if (action.payload.scheduling_rules !== undefined) {
            newData.scheduling_rules = action.payload.scheduling_rules;
          }

          newData.available = true;

          state.data = newData;
        }

        state.error = null;
      })
      .addCase(saveAvailability.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(resetAllStoreData, (state) => {
        return initialState;
      });
  },
});

export const {
  getAvailability,
  deleteAvailability,
  addAvailability,
  updateTimesOptimistic,
  invalidateAvailabilityCache,
  clearAvailabilityError,
} = AvailabilityDataSlice.actions;

export default AvailabilityDataSlice.reducer;
